import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Paperclip, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';

const Attachment = ({ msg }) => {
  const open = async () => {
    try {
      const res = await api.get(`/support/attachment/${msg.id}`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Fichier indisponible'); }
  };
  return (
    <button onClick={open} className="flex items-center space-x-2 mt-1.5 text-xs font-semibold underline opacity-90 hover:opacity-100" data-testid={`attachment-${msg.id}`}>
      <FileText className="w-3.5 h-3.5" /><span className="truncate max-w-[160px]">{msg.attachment.filename}</span>
    </button>
  );
};

export const SupportChat = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const s = t.support || {};
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/support/messages'); setMessages(r.data.messages || []); setUnread(0); } catch {}
  }, []);

  useEffect(() => {
    if (!user || user.role === 'admin') return;
    let iv;
    if (open) { load(); iv = setInterval(load, 5000); }
    else {
      const check = () => api.get('/support/unread').then((r) => setUnread(r.data.unread)).catch(() => {});
      check(); iv = setInterval(check, 15000);
    }
    return () => clearInterval(iv);
  }, [user, open, load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  if (!user || user.role === 'admin') return null;

  const send = async () => {
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('text', text.trim());
      if (file) fd.append('file', file);
      await api.post('/support/messages', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setText(''); setFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error(s.tooBig || 'Fichier trop volumineux (max 10 Mo)'); return; }
    setFile(f);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100" data-testid="support-chat-panel">
            <div className="bg-black p-5 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full gradient-pink-violet flex items-center justify-center text-white font-extrabold">A</div>
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 rounded-full border-2 border-black"></span>
                </div>
                <div>
                  <div className="text-white font-extrabold text-sm" style={{ fontFamily: 'Manrope' }}>{s.title || 'Support alphaswapp'}</div>
                  <div className="text-emerald-400 text-[11px] font-semibold">{s.online || 'En ligne — réponse rapide'}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="close-chat-btn">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="h-80 overflow-y-auto p-4 space-y-3 bg-[#F5F6F7]" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-gray-400 text-sm mt-16 px-6">
                  {s.empty || 'Une question sur votre transaction ? Écrivez-nous, notre équipe vous répond ici.'}
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 text-sm shadow-sm ${m.sender === 'user'
                    ? 'bg-gray-900 text-white rounded-2xl rounded-br-md'
                    : 'bg-white text-gray-900 rounded-2xl rounded-bl-md'}`} data-testid={`msg-${m.id}`}>
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    {m.attachment && <Attachment msg={m} />}
                    <div className={`text-[10px] mt-1 ${m.sender === 'user' ? 'text-white/50' : 'text-gray-400'}`}>
                      {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {file && (
              <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center justify-between text-xs">
                <span className="truncate font-semibold text-amber-800">📎 {file.name}</span>
                <button onClick={() => setFile(null)} className="text-amber-600 font-bold ml-2">✕</button>
              </div>
            )}

            <div className="p-3 border-t border-gray-100 flex items-center space-x-2 bg-white">
              <label className="p-2.5 rounded-full hover:bg-gray-100 cursor-pointer transition-colors shrink-0" data-testid="attach-file-label">
                <Paperclip className="w-4 h-4 text-gray-500" />
                <input type="file" className="hidden" accept="image/*,.pdf" onChange={pickFile} data-testid="chat-file-input" />
              </label>
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
                placeholder={s.placeholder || 'Votre message...'} className="flex-1 bg-[#F5F6F7] rounded-full px-4 py-2.5 text-sm outline-none" data-testid="chat-input" />
              <button onClick={send} disabled={sending || (!text.trim() && !file)}
                className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40 shrink-0" data-testid="chat-send-btn">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <a href="https://t.me/alphaswapp" target="_blank" rel="noreferrer"
              className="block text-center text-[11px] font-semibold text-gray-400 hover:text-gray-600 pb-2.5 bg-white transition-colors" data-testid="chat-telegram-link">
              {s.telegram || 'Ou écrivez-nous sur Telegram : @alphaswapp'}
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }} onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gray-900 text-white shadow-2xl flex items-center justify-center" data-testid="support-chat-bubble">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white text-xs font-extrabold rounded-full flex items-center justify-center border-2 border-white" data-testid="chat-unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </motion.button>
    </>
  );
};

export default SupportChat;
